import { useState, useEffect } from 'react';

/**
 * Custom hook that returns true if the current viewport matches the provided media query
 * @param {string} query - CSS media query string e.g. '(max-width: 767px)'
 * @returns {boolean} Whether the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  // Use useState to store the match state
  const [matches, setMatches] = useState<boolean>(() => {
    // Initialize with the current match on first render (client-side only)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    // Default fallback for SSR
    return false;
  });

  useEffect(() => {
    // Ensure we're in a browser environment
    if (typeof window === 'undefined') {
      return;
    }

    // Create media query list
    const mediaQueryList = window.matchMedia(query);

    // Update matches state initially
    setMatches(mediaQueryList.matches);

    // Create handler function to update state when media query changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add event listener (use the appropriate method based on browser support)
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange);
    } else {
      // For older browsers
      mediaQueryList.addListener(handleChange);
    }

    // Clean up event listener when component unmounts
    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', handleChange);
      } else {
        // For older browsers
        mediaQueryList.removeListener(handleChange);
      }
    };
  }, [query]); // Re-run effect only if query changes

  return matches;
};

export default useMediaQuery;